// src/components/LandingPage.js
import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, Container, IconButton } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import BarChartIcon from '@mui/icons-material/BarChart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleIcon from '@mui/icons-material/People';
import SpeedIcon from '@mui/icons-material/Speed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import CakeIcon from '@mui/icons-material/Cake';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EmailIcon from '@mui/icons-material/Email';
import HandshakeIcon from '@mui/icons-material/Handshake';
import InsightsIcon from '@mui/icons-material/Insights';
import logo from '../assets/logo.png';
import previewImage from '../assets/preview.png';

/* ─── Keyframes ─────────────────────────────────────── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50% { transform: scale(1.05); opacity: 0.7; }
`;

const scrollX = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

/* ─── Styled Components ─────────────────────────────── */

const Nav = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1100,
  transition: 'all 0.3s ease',
  padding: theme.spacing(2, 0),
}));

const NavInner = styled(Container)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const NavLinks = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),
  [theme.breakpoints.down('lg')]: {
    gap: theme.spacing(2.25),
  },
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const NavLink = styled(Typography)(({ theme }) => ({
  color: '#555',
  fontSize: '0.95rem',
  fontWeight: 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  transition: 'color 0.2s ease',
  [theme.breakpoints.down('lg')]: {
    fontSize: '0.85rem',
  },
  '@media (hover: hover)': {
    '&:hover': {
      color: '#ff9e00',
    },
  },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'none',
  color: '#333',
  [theme.breakpoints.down('md')]: {
    display: 'flex',
  },
}));

const MobileMenu = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  zIndex: 1200,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(4),
}));

const PrimaryButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #ffb71b 0%, #ff9e00 100%)',
  color: '#000',
  fontWeight: 700,
  fontSize: '1rem',
  padding: '12px 32px',
  borderRadius: 14,
  textTransform: 'none',
  boxShadow: '0 4px 20px rgba(255, 183, 27, 0.3)',
  transition: 'all 0.3s ease',
  '@media (hover: hover)': {
    '&:hover': {
      background: 'linear-gradient(135deg, #ffc440 0%, #ffb71b 100%)',
      boxShadow: '0 6px 28px rgba(255, 183, 27, 0.45)',
      transform: 'translateY(-2px)',
    },
  },
}));

const SecondaryButton = styled(Button)(() => ({
  color: '#333',
  fontWeight: 600,
  fontSize: '1rem',
  padding: '12px 32px',
  borderRadius: 14,
  textTransform: 'none',
  border: '2px solid #e0e0e0',
  transition: 'all 0.3s ease',
  '@media (hover: hover)': {
    '&:hover': {
      borderColor: '#ffb71b',
      backgroundColor: 'rgba(255, 183, 27, 0.05)',
    },
  },
}));

const GlowOrb = styled(Box)(({ theme }) => ({
  position: 'absolute',
  borderRadius: '50%',
  filter: 'blur(100px)',
  animation: `${pulse} 6s ease-in-out infinite`,
  pointerEvents: 'none',
  [theme.breakpoints.down('lg')]: {
    filter: 'blur(70px)',
    animation: 'none',
  },
}));

// One vertical rhythm for every content section, so the gaps between them are
// identical top to bottom. Note this applies to BOTH edges, so the visible gap
// between two adjacent sections is double these values.
const SECTION_PY = { xs: 5, md: 6, lg: 7 };

// Space under a section's heading block, before its content. Scales with the
// section padding so the two stay in proportion.
const SECTION_HEADER_MB = { xs: 4, md: 5 };

const SectionTitle = styled(Typography)(() => ({
  fontWeight: 800,
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  lineHeight: 1.15,
  marginBottom: 16,
  color: '#111',
}));

const SectionSubtitle = styled(Typography)(() => ({
  fontSize: '1.15rem',
  lineHeight: 1.7,
  maxWidth: 600,
  color: '#5f5f5f',
}));

const FeatureCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #f0f0f0',
  borderRadius: 20,
  padding: theme.spacing(4),
  [theme.breakpoints.between('sm', 'lg')]: {
    padding: theme.spacing(3),
  },
  transition: 'all 0.3s ease',
  cursor: 'default',
  '@media (hover: hover)': {
    '&:hover': {
      border: '1px solid rgba(255, 183, 27, 0.3)',
      transform: 'translateY(-4px)',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.06)',
    },
  },
}));

const FeatureIcon = styled(Box)(({ $bg }) => ({
  width: 56,
  height: 56,
  borderRadius: 16,
  backgroundColor: $bg || '#fff8e7',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 20,
}));

const StatBox = styled(Box)(() => ({
  textAlign: 'center',
  padding: '20px 16px',
}));

const StepNumber = styled(Box)(() => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  background: 'linear-gradient(135deg, #ffb71b 0%, #ff9e00 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 800,
  fontSize: '1.2rem',
  color: '#000',
  marginBottom: 20,
  flexShrink: 0,
}));

const TestimonialCard = styled(Box)(({ theme }) => ({
  background: '#ffffff',
  border: '1px solid #f0f0f0',
  borderRadius: 24,
  padding: theme.spacing(4),
  minWidth: 340,
  maxWidth: 400,
  flexShrink: 0,
  transition: 'all 0.3s ease',
  '@media (hover: hover)': {
    '&:hover': {
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.06)',
      transform: 'translateY(-2px)',
    },
  },
}));

const FooterLink = styled(Typography)(() => ({
  color: '#767676',
  fontSize: '0.9rem',
  cursor: 'pointer',
  transition: 'color 0.2s ease',
  '@media (hover: hover)': {
    '&:hover': {
      color: '#ff9e00',
    },
  },
}));

/* ─── Helper: useInView for scroll animations ────── */
function useInView(options = {}) {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
}

/* ─── Feature icon config (preserved; text comes from CMS) ─── */
const FEATURE_ICONS = [
  { icon: <QrCodeScannerIcon sx={{ fontSize: 28, color: '#ffb71b' }} />, iconBg: '#fff8e7' },
  { icon: <NotificationsActiveIcon sx={{ fontSize: 28, color: '#7c4dff' }} />, iconBg: '#f3e8ff' },
  { icon: <BarChartIcon sx={{ fontSize: 28, color: '#00bcd4' }} />, iconBg: '#e0f7fa' },
  { icon: <StorefrontIcon sx={{ fontSize: 28, color: '#ff5722' }} />, iconBg: '#fbe9e7' },
  { icon: <PeopleIcon sx={{ fontSize: 28, color: '#4caf50' }} />, iconBg: '#e8f5e9' },
  { icon: <LocalOfferIcon sx={{ fontSize: 28, color: '#e91e63' }} />, iconBg: '#fce4ec' },
];

const PRICING_ICONS = [
  { icon: <CardGiftcardIcon sx={{ fontSize: 28, color: '#ffb71b' }} />, iconBg: '#fff8e7' },
  { icon: <InsightsIcon sx={{ fontSize: 28, color: '#7c4dff' }} />, iconBg: '#f3e8ff' },
  { icon: <AutoAwesomeIcon sx={{ fontSize: 28, color: '#ff5722' }} />, iconBg: '#fbe9e7' },
  { icon: <CakeIcon sx={{ fontSize: 28, color: '#e91e63' }} />, iconBg: '#fce4ec' },
  { icon: <EmailIcon sx={{ fontSize: 28, color: '#00bcd4' }} />, iconBg: '#e0f7fa' },
  { icon: <HandshakeIcon sx={{ fontSize: 28, color: '#4caf50' }} />, iconBg: '#e8f5e9' },
];

/* ─── Default content (fallback when no CMS data) ──── */
const DEFAULTS = {
  hero: {
    heading: 'Customer loyalty, powered by ',
    headingHighlight: 'marketing science',
    subheading: "The modern marketing platform based on customer's behavior that helps businesses build lasting relationships, increase sales, digital stamp cards, smart notifications, and powerful analytics — all in one place.",
    cta1Text: 'Register Now',
    cta2Text: 'See How It Works',
    image: '',
    imagePosition: 'right',
  },
  marquee: {
    title: 'Built for every local business',
    businessTypes: ['Cafés', 'Restaurants', 'Hair Salons', 'Nail Salons', 'Gyms', 'Car Washes', 'Pet Groomers', 'Massage Studios', 'Beauty Bars', 'Bakeries', 'Juice Bars', 'Barbershops'],
  },
  features: {
    badge: 'Powerful Features',
    title: 'Everything you need to build ',
    titleHighlight: 'customer loyalty',
    subtitle: 'From digital stamp cards to automated marketing — Rewarding gives you all the tools to keep customers coming back.',
    image: '',
    items: [
      { title: 'Digital Stamp Cards', description: 'Replace paper punch cards with beautiful digital stamp cards. Customers scan a QR code — simple, fast, and eco-friendly.' },
      { title: 'Smart Notifications', description: 'AI Automated and personalized messages for birthdays, visit reminders, and loyalty milestones. Keep customers engaged without lifting a finger.' },
      { title: 'Real-Time Analytics', description: 'Track scans, redemptions, and customer retention with a powerful dashboard. Make data-driven decisions that grow your business.' },
      { title: 'Collaborate with Businesses', description: 'Work with other businesses around you to expand your reach and increase sales.' },
      { title: 'Customer Insights', description: 'Understand your customers better. See who visits most, track repeat rates, and identify your most loyal patrons.' },
      { title: 'Targeted Campaigns', description: 'Launch promotions that reach the right audience. Target by visit frequency, location, and customer behavior.' },
    ],
  },
  personalisation: {
    badge: 'Personalised by AI',
    title: 'Every customer is different. ',
    titleHighlight: 'Every message should be too.',
    body: [
      "Rewarding learns what makes your business unique and what each customer likes, then automatically creates personalised messages that feel like they're coming directly from you.",
      'From birthday wishes and special offers to reminders and thank-you messages, every message is tailored to strengthen relationships and encourage customers to return.',
    ],
    tagline: 'The right message. The right customer. The right time.',
  },
  howItWorks: {
    badge: 'Simple Setup',
    title: 'No technical skills ',
    titleHighlight: 'needed',
    subtitle: 'Get your account live in four simple steps.',
    steps: [
      { title: 'Sign Up in Minutes', description: 'Create your account, set up your business profile, and customise your brand — all in under 5 minutes.' },
      { title: 'Design Your Rewards', description: 'Create digital stamp cards with your logo, colours, and reward tiers. Choose how many stamps earn a reward.' },
      { title: 'Share Your QR Code', description: 'Print your unique QR code or display it digitally. Customers scan it with their phone — no app download needed.' },
      { title: 'Watch Sales Grow', description: 'Track customer visits, see real-time analytics, and watch your repeat business increase week over week.' },
    ],
  },
  dashboard: {
    badge: 'Analytics Dashboard',
    title: 'Know your customers ',
    titleHighlight: 'inside out',
    subtitle: 'Track every scan, redemption, and customer interaction in real-time. Our intuitive dashboard gives you the insights you need to grow.',
    bulletPoints: [
      'Weekly scan trends and customer visit patterns',
      'Customer retention and repeat visit rates',
      'Automated birthday and loyalty notifications',
      'Campaign performance and ROI tracking',
    ],
    image: '',
    imagePosition: 'right',
  },
  testimonials: {
    badge: 'Customer Love',
    title: 'Loved by businesses ',
    titleHighlight: 'everywhere',
    subtitle: 'See what business owners are saying about Rewarding.',
    items: [
      { name: 'Sarah M.', role: 'Café Owner', stars: 5, quote: 'Rewarding transformed our customer retention. We saw a 40% increase in repeat visits within the first month. The digital stamp cards are a game-changer.' },
      { name: 'James L.', role: 'Barber Shop', stars: 5, quote: "Our clients love the simplicity. No more lost paper cards — just a quick scan and they're on their way. The birthday notifications are a brilliant touch." },
      { name: 'Priya K.', role: 'Nail Salon', stars: 5, quote: 'The analytics dashboard gives us insights we never had before. We can see exactly which promotions work and adjust our strategy in real-time.' },
      { name: 'Tom W.', role: 'Pizza Restaurant', stars: 5, quote: 'Setting up was incredibly easy. Within 10 minutes we had our QR code printed and customers were scanning. The automated reminders bring people back.' },
    ],
  },
  pricing: {
    badge: 'Simple Pricing',
    title: '',
    titleHighlight: 'Everything You Need',
    subtitle: 'Turn one-time visitors into loyal customers with rewards, automated AI smart messages, and local collaborations.',
    price: '$49.99',
    priceSubtext: 'No lock-in contract \u2022 Cancel anytime',
    ctaText: 'Get Started Now',
    features: [
      { title: 'Smart Rewards Program', description: 'Launch your own digital rewards system in minutes. Keep customers coming back automatically.', bullets: [], footer: '' },
      { title: 'Customer Data & Insights', description: '', bullets: ['Who visits most', "Who hasn't returned", 'Buying patterns & trends'], footer: '' },
      { title: 'AI "We Miss You" Reminders', description: "Automatically send personalised messages to customers who haven't visited in a while. Bring them back — without lifting a finger.", bullets: [], footer: '' },
      { title: 'Automated Birthday Messages', description: '', bullets: ['A birthday greeting OR', 'A birthday offer to drive visits'], footer: 'Set it once. It runs forever.' },
      { title: 'Monthly Thank You / Offer Message', description: 'Send one free campaign per month to all your customers. Perfect for:', bullets: ['Promotions', 'New arrivals', 'Seasonal offers', 'Slow periods'], footer: '' },
      { title: 'Business Collaboration Feature', description: '', bullets: ['Partner with nearby businesses.', 'When customers earn a reward at your store, they receive an exclusive offer from another local business — and vice versa.', 'Increase exposure. Increase sales. Together.'], footer: '' },
    ],
    replacesTitle: 'Rewarding replaces:',
    replacesItems: ['SMS marketing software', 'Loyalty card systems', 'Email marketing tools', 'CRM software', 'Paid ads for retention'],
  },
  cta: {
    heading: 'Ready to grow your ',
    headingHighlight: 'loyal customer base?',
    subheading: 'Join hundreds of businesses already using Rewarding to increase customer retention and drive repeat visits.',
    cta1Text: 'Register Now',
    cta2Text: 'Sign In',
  },
  footer: {
    description: 'The modern loyalty platform for local businesses. Build lasting customer relationships with digital stamp cards and smart automation.',
    companyLinks: ['Blog', 'Contact'],
    supportLinks: ['Privacy Policy', 'Terms of Service', 'support@rewarding.com'],
  },
};

function deepMerge(defaults, overrides) {
  if (!overrides || typeof overrides !== 'object') return defaults;
  const result = { ...defaults };
  for (const key of Object.keys(defaults)) {
    if (overrides[key] === undefined || overrides[key] === null) continue;
    if (Array.isArray(defaults[key])) {
      result[key] = overrides[key];
    } else if (typeof defaults[key] === 'object' && !Array.isArray(defaults[key])) {
      result[key] = deepMerge(defaults[key], overrides[key]);
    } else {
      result[key] = overrides[key];
    }
  }
  return result;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

/* Map a footer link label to a real destination. Returns a route ('/x'),
   an in-page section ('#id'), a 'mailto:'/'http' URL, or null when there is
   no page for it (so the link can be hidden instead of dead-ending). */
function resolveFooterLink(link) {
  const l = (link || '').toLowerCase().trim();
  if (!l) return null;
  if (l.includes('@')) return `mailto:${(link || '').trim()}`;
  if (l.includes('privacy')) return '/privacy';
  if (l.includes('terms')) return '/terms';
  if (l.includes('blog')) return '/blogs';
  if (l.includes('contact')) return '/contact';
  if (l.includes('feature')) return '#features';
  if (l.includes('how it works') || l.includes('how-it-works')) return '#how-it-works';
  if (l.includes('pricing')) return '#pricing';
  return null; // e.g. About Us, Careers, Help Center — no page exists
}

/* ─── Component ─────────────────────────────────────── */
export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [c, setC] = useState(DEFAULTS);
  const [businessCategories, setBusinessCategories] = useState(DEFAULTS.marquee.businessTypes);

  const [heroRef, heroVisible] = useInView();
  const [featRef, featVisible] = useInView();
  const [personalRef, personalVisible] = useInView();
  const [howRef, howVisible] = useInView();
  const [testRef, testVisible] = useInView();
  const [priceRef, priceVisible] = useInView();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/landing-content`)
      .then(r => r.json())
      .then(data => { if (data && Object.keys(data).length > 1) setC(deepMerge(DEFAULTS, data)); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/api/business-categories`)
      .then(r => r.json())
      .then(data => {
        if (data && Array.isArray(data.categories) && data.categories.length > 0) {
          setBusinessCategories(data.categories);
        }
      })
      .catch(() => {});
  }, []);

  const scrollTo = (id) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Build the props for a dynamic footer link, or null if it has no destination.
  const footerLinkProps = (link) => {
    const to = resolveFooterLink(link);
    if (!to) return null;
    if (to.startsWith('mailto:') || to.startsWith('http')) {
      return { component: 'a', href: to, sx: { textDecoration: 'none' } };
    }
    if (to.startsWith('#')) return { onClick: () => scrollTo(to.slice(1)) };
    return { onClick: () => navigate(to) };
  };

  return (
    <Box sx={{ backgroundColor: '#ffffff', overflowX: 'hidden' }}>

      {/* ━━ NAVIGATION ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Nav
        sx={{
          backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid #f0f0f0' : 'none',
        }}
      >
        <NavInner maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }} onClick={() => scrollTo('hero')}>
            <Box component="img" src={logo} alt="Rewarding" sx={{ height: 36 }} />
            <Typography sx={{ fontWeight: 800, fontSize: '1.3rem', color: '#111', letterSpacing: '-0.02em' }}>
              Rewarding
            </Typography>
          </Box>

          <NavLinks>
            <NavLink onClick={() => scrollTo('features')}>Features</NavLink>
            <NavLink onClick={() => scrollTo('how-it-works')}>How It Works</NavLink>
            <NavLink onClick={() => scrollTo('testimonials')}>Testimonials</NavLink>
            <NavLink onClick={() => scrollTo('pricing')}>Pricing</NavLink>
            <NavLink onClick={() => navigate('/blogs')}>Blog</NavLink>
            <NavLink onClick={() => navigate('/contact')}>Contact</NavLink>
          </NavLinks>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              onClick={() => navigate('/login')}
              sx={{
                color: '#333',
                textTransform: 'none',
                fontWeight: 600,
                display: { xs: 'none', sm: 'inline-flex' },
                '@media (hover: hover)': { '&:hover': { color: '#ff9e00' } },
              }}
            >
              Log In
            </Button>
            <PrimaryButton
              onClick={() => navigate('/register')}
              sx={{
                px: { sm: 2.25, lg: 3 },
                py: 1.2,
                fontSize: { sm: '0.85rem', lg: '0.9rem' },
                whiteSpace: 'nowrap',
                display: { xs: 'none', sm: 'inline-flex' },
              }}
            >
              Register Now
            </PrimaryButton>
            <MobileMenuButton onClick={() => setMobileOpen(true)}>
              <MenuIcon />
            </MobileMenuButton>
          </Box>
        </NavInner>
      </Nav>

      {/* ━━ MOBILE MENU ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {mobileOpen && (
        <MobileMenu>
          <IconButton onClick={() => setMobileOpen(false)} sx={{ position: 'absolute', top: 20, right: 20, color: '#333' }}>
            <CloseIcon />
          </IconButton>
          {['features', 'how-it-works', 'testimonials', 'pricing'].map((id) => (
            <Typography
              key={id}
              onClick={() => scrollTo(id)}
              sx={{ color: '#222', fontSize: '1.5rem', fontWeight: 600, cursor: 'pointer', textTransform: 'capitalize' }}
            >
              {id.replace('-', ' ')}
            </Typography>
          ))}
          <Typography
            onClick={() => { setMobileOpen(false); navigate('/blogs'); }}
            sx={{ color: '#222', fontSize: '1.5rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Blog
          </Typography>
          <Typography
            onClick={() => { setMobileOpen(false); navigate('/contact'); }}
            sx={{ color: '#222', fontSize: '1.5rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Contact
          </Typography>
          <PrimaryButton onClick={() => { setMobileOpen(false); navigate('/register'); }} sx={{ mt: 2 }}>
            Register Now
          </PrimaryButton>
          <Button onClick={() => { setMobileOpen(false); navigate('/login'); }} sx={{ color: '#333', textTransform: 'none', fontWeight: 600 }}>
            Log In
          </Button>
        </MobileMenu>
      )}

      {/* ━━ HERO SECTION ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Box
        id="hero"
        ref={heroRef}
        sx={{
          position: 'relative',
          minHeight: { xs: 'auto', lg: '100vh' },
          display: 'flex',
          alignItems: 'center',
          // The nav is position:fixed, so the hero has to reserve its height at
          // every breakpoint — otherwise centred content taller than the
          // viewport slides up underneath it and gets clipped.
          pt: { xs: 12, sm: 14, lg: 13 },
          pb: { xs: 8, sm: 10, lg: 8 },
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #fffcf5 0%, #ffffff 60%)',
          // On a short laptop screen 100vh is less than the content needs, so
          // let the section grow instead of overflowing.
          '@media (max-height: 820px)': {
            minHeight: 'auto',
          },
        }}
      >
        {/* Background orbs */}
        <GlowOrb sx={{ width: 600, height: 600, top: '-15%', right: '-10%', background: 'rgba(255, 183, 27, 0.12)' }} />
        <GlowOrb sx={{ width: 500, height: 500, bottom: '0%', left: '-15%', background: 'rgba(255, 158, 0, 0.08)', animationDelay: '3s' }} />
        <GlowOrb sx={{ width: 350, height: 350, top: '35%', right: '15%', background: 'rgba(255, 183, 27, 0.06)', animationDelay: '1.5s' }} />

        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: { xs: 6, md: 5, lg: 8 },
              alignItems: 'center',
            }}
          >
            {/* Left content */}
            <Box sx={{ animation: heroVisible ? `${fadeInUp} 0.8s ease-out` : 'none', opacity: heroVisible ? 1 : 0, order: { xs: 1, md: c.hero.imagePosition === 'left' ? 2 : 1 } }}>
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', sm: '3.2rem', md: '2.9rem', lg: '3.8rem' },
                  color: '#111',
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.03em',
                  '@media (max-height: 820px)': {
                    fontSize: '2.9rem',
                    mb: 2,
                  },
                }}
              >
                {c.hero.heading}
                <Box
                  component="span"
                  sx={{ color: '#ff9e00' }}
                >
                  {c.hero.headingHighlight}
                </Box>
              </Typography>

              <Typography
                sx={{
                  fontSize: { xs: '1.05rem', md: '1.1rem', lg: '1.2rem' },
                  color: '#5f5f5f',
                  lineHeight: 1.7,
                  mb: 4,
                  maxWidth: 540,
                  '@media (max-height: 820px)': {
                    fontSize: '1.02rem',
                    lineHeight: 1.6,
                    mb: 3,
                  },
                }}
              >
                {c.hero.subheading}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  // Stack full-width on phones so each action is an easy tap
                  // target; side by side from tablet up.
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'stretch', sm: 'center' },
                  gap: 2,
                  flexWrap: 'wrap',
                }}
              >
                <PrimaryButton
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/register')}
                  sx={{ fontSize: '1.05rem', px: 4, py: 1.5 }}
                >
                  {c.hero.cta1Text}
                </PrimaryButton>

                {/* Phone-only. The nav's Log In is hidden below sm (behind the
                    hamburger), so a returning customer has no visible way in
                    from the hero without this. */}
                <SecondaryButton
                  onClick={() => navigate('/login')}
                  sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                >
                  Sign In
                </SecondaryButton>

                <SecondaryButton onClick={() => scrollTo('how-it-works')}>
                  {c.hero.cta2Text}
                </SecondaryButton>
              </Box>

            </Box>

            {/* Hero image */}
            <Box
              sx={{
                animation: heroVisible ? `${fadeInUp} 1s ease-out 0.2s both` : 'none',
                opacity: heroVisible ? undefined : 0,
                display: 'block',
                width: '100%',
                maxWidth: { xs: '100%', sm: 560, md: '100%' },
                mx: { xs: 0, sm: 'auto', md: 0 },
                order: { xs: 2, md: c.hero.imagePosition === 'left' ? 1 : 2 },
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  animation: `${float} 6s ease-in-out infinite`,
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: -24,
                    borderRadius: 6,
                    background: 'linear-gradient(135deg, rgba(255,183,27,0.15) 0%, rgba(255,158,0,0.08) 100%)',
                    filter: 'blur(40px)',
                  }}
                />
                <Box
                  component="img"
                  src={c.hero.image || previewImage}
                  alt="Rewarding Dashboard"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 40px 80px rgba(0,0,0,0.12)',
                    border: '1px solid #e8e8e8',
                    position: 'relative',
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Container>

        {/* Scroll indicator */}
        <Box
          onClick={() => scrollTo('partners')}
          sx={{
            position: 'absolute',
            bottom: 28,
            left: '50%',
            transform: 'translateX(-50%)',
            cursor: 'pointer',
            animation: `${float} 2s ease-in-out infinite`,
            display: { xs: 'none', lg: 'flex' },
            // No room for it once the hero stops being a full viewport tall.
            '@media (max-height: 820px)': {
              display: 'none',
            },
            flexDirection: 'column',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography sx={{ fontSize: '0.75rem', color: '#bbb', fontWeight: 500, letterSpacing: 2, textTransform: 'uppercase' }}>
            Scroll
          </Typography>
          <KeyboardArrowDownIcon sx={{ color: '#bbb', fontSize: 20 }} />
        </Box>
      </Box>

      {/* ━━ PARTNERS / BUSINESS TYPES MARQUEE ━━━━━━━━━ */}
      <Box
        id="partners"
        sx={{
          py: { xs: 5, md: 6 },
          borderTop: '1px solid #f5f5f5',
          borderBottom: '1px solid #f5f5f5',
          background: '#fafafa',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="xl">
          <Typography
            sx={{ textAlign: 'center', color: '#bbb', fontSize: '0.9rem', fontWeight: 600, mb: 4, textTransform: 'uppercase', letterSpacing: 3 }}
          >
            {c.marquee.title}
          </Typography>
        </Container>
        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
          <Box
            sx={{
              display: 'flex',
              animation: `${scrollX} 30s linear infinite`,
              gap: 3,
              width: 'max-content',
            }}
          >
            {[...businessCategories, ...businessCategories].map((type, i) => (
              <Box
                key={i}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 10,
                  border: '1px solid #e8e8e8',
                  background: '#fff',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease',
                  '&:hover': { borderColor: '#ffb71b', background: '#fffdf5' },
                }}
              >
                <Typography sx={{ color: '#666', fontWeight: 600, fontSize: '0.95rem' }}>
                  {type}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* ━━ FEATURES SECTION ━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Box id="features" ref={featRef} sx={{ py: SECTION_PY, position: 'relative', background: '#fff' }}>
        <GlowOrb sx={{ width: 500, height: 500, top: '5%', left: '-15%', background: 'rgba(255, 183, 27, 0.06)' }} />

        <Container maxWidth="xl">
          <Box
            sx={{
              textAlign: 'center',
              mb: SECTION_HEADER_MB,
              animation: featVisible ? `${fadeInUp} 0.7s ease-out` : 'none',
              opacity: featVisible ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: '#fff8e7',
                border: '1px solid #ffe0a0',
                borderRadius: 8,
                px: 2,
                py: 0.5,
                mb: 3,
              }}
            >
              <SpeedIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
                {c.features.badge}
              </Typography>
            </Box>
            <SectionTitle>
              {c.features.title}
              <Box component="span" sx={{ color: '#ff9e00' }}>{c.features.titleHighlight}</Box>
            </SectionTitle>
            <SectionSubtitle sx={{ mx: 'auto' }}>
              {c.features.subtitle}
            </SectionSubtitle>
          </Box>

          {/* Product preview. Moved here from the old dashboard section; still
              CMS-managed via features.image, falling back to the bundled shot. */}
          <Box
            sx={{
              mb: { xs: 4, md: 6 },
              mx: 'auto',
              maxWidth: 980,
              animation: featVisible ? `${fadeInUp} 0.8s ease-out 0.15s both` : 'none',
              opacity: featVisible ? undefined : 0,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                borderRadius: 5,
                overflow: 'hidden',
                border: '1px solid #e8e8e8',
                boxShadow: '0 40px 80px rgba(0,0,0,0.08)',
              }}
            >
              <Box
                component="img"
                src={c.features.image || previewImage}
                alt="Rewarding product preview"
                sx={{ width: '100%', display: 'block' }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: { xs: 3, md: 2.5, lg: 3 },
            }}
          >
            {c.features.items.map((feat, i) => {
              const ic = FEATURE_ICONS[i] || FEATURE_ICONS[0];
              return (
                <FeatureCard
                  key={i}
                  sx={{
                    animation: featVisible ? `${fadeInUp} 0.6s ease-out ${i * 0.1}s both` : 'none',
                    opacity: featVisible ? undefined : 0,
                  }}
                >
                  <FeatureIcon $bg={ic.iconBg}>{ic.icon}</FeatureIcon>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', color: '#222', mb: 1.5 }}>
                    {feat.title}
                  </Typography>
                  <Typography sx={{ color: '#6e6e6e', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {feat.description}
                  </Typography>
                </FeatureCard>
              );
            })}
          </Box>
        </Container>
      </Box>

      {/* ━━ PERSONALISATION ━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      {/* Sits between Features (white) and How It Works (grey). A warm band
          keeps the three sections visually distinct from one another. */}
      <Box
        id="personalisation"
        ref={personalRef}
        sx={{
          py: SECTION_PY,
          position: 'relative',
          overflow: 'hidden',
          background: 'linear-gradient(180deg, #fffdf5 0%, #fffaf0 100%)',
          borderTop: '1px solid #f7f0e0',
          borderBottom: '1px solid #f7f0e0',
        }}
      >
        <GlowOrb sx={{ width: 460, height: 460, top: '-30%', right: '-8%', background: 'rgba(255, 183, 27, 0.10)' }} />

        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              textAlign: 'center',
              animation: personalVisible ? `${fadeInUp} 0.7s ease-out` : 'none',
              opacity: personalVisible ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: '#fff8e7',
                border: '1px solid #ffe0a0',
                borderRadius: 8,
                px: 2,
                py: 0.5,
                mb: 3,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
                {c.personalisation.badge}
              </Typography>
            </Box>

            <SectionTitle>
              {c.personalisation.title}
              <Box component="span" sx={{ color: '#ff9e00' }}>{c.personalisation.titleHighlight}</Box>
            </SectionTitle>

            <Box sx={{ maxWidth: 720, mx: 'auto', mt: 3 }}>
              {(c.personalisation.body || []).map((paragraph, i) => (
                <Typography
                  key={i}
                  sx={{
                    fontSize: { xs: '1.02rem', md: '1.12rem' },
                    lineHeight: 1.75,
                    color: '#5f5f5f',
                    mb: 2.5,
                    animation: personalVisible ? `${fadeInUp} 0.6s ease-out ${0.15 + i * 0.1}s both` : 'none',
                    opacity: personalVisible ? undefined : 0,
                  }}
                >
                  {paragraph}
                </Typography>
              ))}
            </Box>

            {c.personalisation.tagline && (
              <Typography
                sx={{
                  mt: 4,
                  fontWeight: 800,
                  fontSize: { xs: '1.15rem', md: '1.4rem' },
                  color: '#111',
                  letterSpacing: '-0.01em',
                  animation: personalVisible ? `${fadeInUp} 0.6s ease-out 0.4s both` : 'none',
                  opacity: personalVisible ? undefined : 0,
                }}
              >
                {c.personalisation.tagline}
              </Typography>
            )}
          </Box>
        </Container>
      </Box>

      {/* ━━ HOW IT WORKS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Box
        id="how-it-works"
        ref={howRef}
        sx={{
          py: SECTION_PY,
          background: '#fafafa',
          position: 'relative',
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              textAlign: 'center',
              mb: SECTION_HEADER_MB,
              animation: howVisible ? `${fadeInUp} 0.7s ease-out` : 'none',
              opacity: howVisible ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: '#fff8e7',
                border: '1px solid #ffe0a0',
                borderRadius: 8,
                px: 2,
                py: 0.5,
                mb: 3,
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
                {c.howItWorks.badge}
              </Typography>
            </Box>
            <SectionTitle>
              {c.howItWorks.title}
              <Box component="span" sx={{ color: '#ff9e00' }}>{c.howItWorks.titleHighlight}</Box>
            </SectionTitle>
            <SectionSubtitle sx={{ mx: 'auto' }}>
              {c.howItWorks.subtitle}
            </SectionSubtitle>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: { xs: 3, lg: 4 } }}>
            {c.howItWorks.steps.map((step, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  gap: { xs: 2, lg: 3 },
                  p: { xs: 3, lg: 4 },
                  borderRadius: 4,
                  background: '#fff',
                  border: '1px solid #f0f0f0',
                  transition: 'all 0.3s ease',
                  animation: howVisible ? `${fadeInUp} 0.6s ease-out ${i * 0.15}s both` : 'none',
                  opacity: howVisible ? undefined : 0,
                  '@media (hover: hover)': {
                    '&:hover': {
                      border: '1px solid rgba(255,183,27,0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 32px rgba(0, 0, 0, 0.05)',
                    },
                  },
                }}
              >
                <StepNumber>{i + 1}</StepNumber>
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: '#222', mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ color: '#6e6e6e', lineHeight: 1.7, fontSize: '0.95rem' }}>
                    {step.description}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ━━ TESTIMONIALS ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Box id="testimonials" ref={testRef} sx={{ py: SECTION_PY, overflow: 'hidden', background: '#fafafa' }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              textAlign: 'center',
              mb: SECTION_HEADER_MB,
              animation: testVisible ? `${fadeInUp} 0.7s ease-out` : 'none',
              opacity: testVisible ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: '#fff8e7',
                border: '1px solid #ffe0a0',
                borderRadius: 8,
                px: 2,
                py: 0.5,
                mb: 3,
              }}
            >
              <StarIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
                {c.testimonials.badge}
              </Typography>
            </Box>
            <SectionTitle>
              {c.testimonials.title}
              <Box component="span" sx={{ color: '#ff9e00' }}>{c.testimonials.titleHighlight}</Box>
            </SectionTitle>
            <SectionSubtitle sx={{ mx: 'auto' }}>
              {c.testimonials.subtitle}
            </SectionSubtitle>
          </Box>
        </Container>

        <Box
          sx={{
            display: 'flex',
            gap: 3,
            px: 3,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
            scrollSnapType: 'x mandatory',
            '& > *': { scrollSnapAlign: 'center' },
            '&::-webkit-scrollbar': { display: 'none' },
            pb: 2,
          }}
        >
          {c.testimonials.items.map((t, i) => (
            <TestimonialCard
              key={i}
              sx={{
                animation: testVisible ? `${fadeInUp} 0.6s ease-out ${i * 0.12}s both` : 'none',
                opacity: testVisible ? undefined : 0,
              }}
            >
              <Box sx={{ display: 'flex', gap: 0.5, mb: 2 }}>
                {Array.from({ length: t.stars }).map((_, j) => (
                  <StarIcon key={j} sx={{ fontSize: 18, color: '#ffb71b' }} />
                ))}
              </Box>
              <Typography sx={{ color: '#555', lineHeight: 1.7, fontSize: '0.95rem', mb: 3, fontStyle: 'italic' }}>
                &ldquo;{t.quote || t.text}&rdquo;
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: 'linear-gradient(135deg, #ffb71b 0%, #ff9e00 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#000' }}>
                    {t.name[0]}
                  </Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#222', fontSize: '0.95rem' }}>{t.name}</Typography>
                  <Typography sx={{ color: '#aaa', fontSize: '0.8rem' }}>{t.role}</Typography>
                </Box>
              </Box>
            </TestimonialCard>
          ))}
        </Box>
      </Box>

      {/* ━━ PRICING SECTION ━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Box
        id="pricing"
        ref={priceRef}
        sx={{
          py: SECTION_PY,
          position: 'relative',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        <GlowOrb sx={{ width: 500, height: 500, top: '10%', left: '-10%', background: 'rgba(255,183,27,0.08)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          {/* Header */}
          <Box
            sx={{
              textAlign: 'center',
              mb: SECTION_HEADER_MB,
              animation: priceVisible ? `${fadeInUp} 0.7s ease-out` : 'none',
              opacity: priceVisible ? 1 : 0,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                background: '#fff8e7',
                border: '1px solid #ffe0a0',
                borderRadius: 8,
                px: 2,
                py: 0.5,
                mb: 3,
              }}
            >
              <AttachMoneyIcon sx={{ fontSize: 16, color: '#ffb71b' }} />
              <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#b07800' }}>
                {c.pricing.badge}
              </Typography>
            </Box>
            <SectionTitle>
              {c.pricing.title ? <>{c.pricing.title}{' '}</> : null}
              <Box component="span" sx={{ color: '#ff9e00' }}>{c.pricing.titleHighlight}</Box>
              {!c.pricing.title ? ' to Grow Sales.' : ''}
            </SectionTitle>
            <SectionSubtitle sx={{ mx: 'auto' }}>
              {c.pricing.subtitle}
            </SectionSubtitle>
          </Box>

          {/* Feature Cards Grid — skipped entirely when none are configured,
              otherwise it leaves an empty block and a stray gap. */}
          {(c.pricing.features || []).length > 0 && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
              gap: 3,
              mb: SECTION_HEADER_MB,
            }}
          >
            {c.pricing.features.map((feat, i) => {
              const pic = PRICING_ICONS[i] || PRICING_ICONS[0];
              return { ...feat, icon: pic.icon, iconBg: pic.iconBg, desc: feat.description };
            }).map((feat, i) => (
              <FeatureCard
                key={i}
                sx={{
                  animation: priceVisible ? `${fadeInUp} 0.6s ease-out ${0.2 + i * 0.08}s both` : 'none',
                  opacity: priceVisible ? undefined : 0,
                }}
              >
                <FeatureIcon $bg={feat.iconBg}>{feat.icon}</FeatureIcon>
                <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#222', mb: 1.5 }}>
                  {feat.title}
                </Typography>
                {feat.desc && (
                  <Typography sx={{ color: '#6e6e6e', lineHeight: 1.7, fontSize: '0.9rem', mb: feat.bullets ? 1 : 0 }}>
                    {feat.desc}
                  </Typography>
                )}
                {feat.bullets && (
                  <Box component="ul" sx={{ m: 0, pl: 2.5, color: '#6e6e6e', fontSize: '0.9rem', lineHeight: 1.8 }}>
                    {feat.bullets.map((b, j) => (
                      <li key={j}>{b}</li>
                    ))}
                  </Box>
                )}
                {feat.footer && (
                  <Typography sx={{ color: '#ffb71b', fontWeight: 600, fontSize: '0.85rem', mt: 1.5 }}>
                    {feat.footer}
                  </Typography>
                )}
              </FeatureCard>
            ))}
          </Box>
          )}

          {/* Price + inclusions, in one card. The price used to sit in its own
              box above this list, and a separate "Pays for Itself" panel sat
              alongside; both are folded away so the section reads as a single
              offer. */}
          <Box
            sx={{
              maxWidth: 680,
              mx: 'auto',
              mb: SECTION_HEADER_MB,
              borderRadius: 6,
              overflow: 'hidden',
              border: '2px solid #ffe0a0',
              background: '#fff',
              boxShadow: '0 24px 60px rgba(255, 183, 27, 0.10)',
              animation: priceVisible ? `${fadeInUp} 0.7s ease-out 0.15s both` : 'none',
              opacity: priceVisible ? undefined : 0,
            }}
          >
            {/* Price */}
            <Box
              sx={{
                textAlign: 'center',
                px: { xs: 3, md: 6 },
                pt: { xs: 4, md: 5 },
                pb: { xs: 3.5, md: 4 },
                background: 'linear-gradient(135deg, #fff8e7 0%, #fffdf5 50%, #fff3e0 100%)',
              }}
            >
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '3rem', md: '4rem' }, color: '#111', lineHeight: 1 }}>
                {c.pricing.price}
                <Typography component="span" sx={{ fontWeight: 500, fontSize: '1.2rem', color: '#6e6e6e' }}> / month</Typography>
              </Typography>
              <Typography sx={{ color: '#767676', fontSize: { xs: '0.9rem', md: '1rem' }, mt: 1.5 }}>
                {c.pricing.priceSubtext}
              </Typography>
            </Box>

            {/* Inclusions */}
            <Box sx={{ px: { xs: 3, md: 6 }, py: { xs: 3.5, md: 4 }, borderTop: '1px solid #ffe0a0' }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: '1.15rem',
                  color: '#222',
                  textAlign: 'center',
                  mb: 3,
                }}
              >
                {c.pricing.replacesTitle}
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 3,
                  rowGap: 1.75,
                }}
              >
                {(c.pricing.replacesItems || []).map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <CheckCircleIcon sx={{ fontSize: 20, color: '#4caf50', mt: '2px', flexShrink: 0 }} />
                    <Typography sx={{ color: '#5f5f5f', fontSize: '0.98rem', lineHeight: 1.5 }}>{item}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>


          {/* CTA */}
          <Box
            sx={{
              textAlign: 'center',
              animation: priceVisible ? `${fadeInUp} 0.7s ease-out 0.6s both` : 'none',
              opacity: priceVisible ? undefined : 0,
            }}
          >
            <PrimaryButton
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate('/register')}
              sx={{ fontSize: '1.1rem', px: 5, py: 1.8 }}
            >
              {c.pricing.ctaText}
            </PrimaryButton>
          </Box>
        </Container>
      </Box>

      {/* ━━ FOOTER ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
      <Box
        sx={{
          borderTop: '1px solid #f0f0f0',
          py: 6,
          background: '#fafafa',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '2fr 1fr 1fr 1fr' },
              gap: { xs: 4, sm: 5, md: 6, lg: 8 },
              mb: SECTION_HEADER_MB,
            }}
          >
            {/* Brand column */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box component="img" src={logo} alt="Rewarding" sx={{ height: 32 }} />
                <Typography sx={{ fontWeight: 800, fontSize: '1.2rem', color: '#222' }}>Rewarding</Typography>
              </Box>
              <Typography sx={{ color: '#767676', fontSize: '0.9rem', lineHeight: 1.7, maxWidth: 300 }}>
                {c.footer.description}
              </Typography>
            </Box>

            {/* Product */}
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.9rem', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                Product
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <FooterLink onClick={() => scrollTo('features')}>Features</FooterLink>
                <FooterLink onClick={() => scrollTo('how-it-works')}>How It Works</FooterLink>
                <FooterLink onClick={() => scrollTo('pricing')}>Pricing</FooterLink>
              </Box>
            </Box>

            {/* Company */}
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.9rem', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                Company
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {c.footer.companyLinks.map((link, i) => {
                  const props = footerLinkProps(link);
                  if (!props) return null; // hide links that have no real page
                  return <FooterLink key={i} {...props}>{link}</FooterLink>;
                })}
              </Box>
            </Box>

            {/* Support */}
            <Box>
              <Typography sx={{ fontWeight: 700, color: '#333', fontSize: '0.9rem', mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
                Support
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {c.footer.supportLinks.map((link, i) => {
                  const props = footerLinkProps(link);
                  if (!props) return null; // hide links that have no real page
                  return <FooterLink key={i} {...props}>{link}</FooterLink>;
                })}
              </Box>
            </Box>
          </Box>

          {/* Bottom bar */}
          <Box
            sx={{
              borderTop: '1px solid #e8e8e8',
              pt: 4,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Typography sx={{ color: '#ccc', fontSize: '0.85rem' }}>
              &copy; {new Date().getFullYear()} Rewarding. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FooterLink sx={{ fontSize: '0.8rem' }} onClick={() => navigate('/privacy')}>Privacy</FooterLink>
              <FooterLink sx={{ fontSize: '0.8rem' }} onClick={() => navigate('/terms')}>Terms</FooterLink>
              <FooterLink sx={{ fontSize: '0.8rem' }}>Cookies</FooterLink>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
